import { Tooltip } from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import { EAllowStatus, ELogicalOperator } from '~/src/dash/apiV2/targeting/targetingPresets';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { IndependentSelect } from '~/src/dash/components/dropdowns/IndependentSelect';
import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import { IncludeExcludeToggle } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/IncludeExcludeToggle';
import { LogicToggle } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/LogicToggle';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { Col, Cols, spacing } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

export interface ISelectWithIncludeAndLogicProps {
  allowKey?: string;
  convertResponseValue?: (value: any) => Array<number | string>;
  convertSelectedValue?: (value: Set<number | string>) => any;
  displayID?: boolean;
  enableActiveFilter?: boolean;
  enableLabelTooltip?: boolean;
  isSelectionRequired?: boolean;
  listKey: string;
  listLabel: string;
  logicKey?: string;
  placeholder: string;
  selectedLimit?: number;
  singularLabel: string;
  valueKey: string;
  key: string; // MUST BE UNIQUE
  disableSort?: boolean;
  tooltip?: string;
  onLoadDisplayActiveOnly?: boolean; // MUST HAVE isActive PROP IN OPTIONS
  extractStringToSort?: (option: ISelectOption) => string; // use only if you need to alter the label or change what string is sorted
}

export const SelectWithIncludeAndLogic = ({
  allowKey,
  convertResponseValue,
  convertSelectedValue,
  displayID,
  enableActiveFilter,
  enableLabelTooltip,
  isSelectionRequired,
  listKey,
  listLabel,
  logicKey,
  placeholder,
  selectedLimit,
  singularLabel,
  valueKey,
  disableSort = false,
  tooltip,
  onLoadDisplayActiveOnly = false,
  extractStringToSort,
}: ISelectWithIncludeAndLogicProps) => {
  const { object, defaultObject, lists } = useFormBuilderStore();

  const [status, setStatus] = useState<EAllowStatus>(allowKey ? object[allowKey] : '');
  const [operator, setOperator] = useState<ELogicalOperator>(logicKey ? object[logicKey] : '');

  const selectedOptionIDsArray = convertResponseValue
    ? convertResponseValue(object[valueKey])
    : object[valueKey];

  useEffect(() => {
    return () => {
      object[valueKey] = defaultObject[valueKey];
      if (allowKey) object[allowKey] = defaultObject[allowKey];
      if (logicKey) object[logicKey] = defaultObject[logicKey];
    };
  }, []);

  const handleApplySelect = (selectedIDs: Set<number | string>) => {
    const convertedValue = convertSelectedValue
      ? convertSelectedValue(selectedIDs)
      : Array.from(selectedIDs);
    object[valueKey] = convertedValue;
  };

  const handleToggleStatus = () => {
    if (allowKey) {
      const newStatus = status === EAllowStatus.ALLOW ? EAllowStatus.DENY : EAllowStatus.ALLOW;
      object[allowKey] = newStatus;
      setStatus(newStatus);
    }
  };

  const handleToggleOperator = () => {
    if (logicKey) {
      const newOperator =
        operator === ELogicalOperator.AND ? ELogicalOperator.OR : ELogicalOperator.AND;
      object[logicKey] = newOperator;
      setOperator(newOperator);
    }
  };

  return (
    <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
      <Col flex style={{ maxWidth: '480px' }}>
        <IndependentSelect
          displayID={displayID}
          enableActiveFilter={enableActiveFilter}
          enableLabelTooltip={enableLabelTooltip}
          handleApplySelect={handleApplySelect}
          isSelectionRequired={isSelectionRequired}
          listLabel={listLabel}
          options={lists[listKey]}
          placeholder={placeholder}
          selectedLimit={selectedLimit}
          selectedOptionIDs={new Set(selectedOptionIDsArray)}
          singularLabel={singularLabel}
          key={listKey}
          disableSort={disableSort}
          maxWidth={480}
          onLoadDisplayActiveOnly={onLoadDisplayActiveOnly}
          extractStringToSort={extractStringToSort}
        />
      </Col>
      {tooltip && (
        <Col>
          <Tooltip title={tooltip} placement='top' interactive>
            <div style={{ display: 'flex' }}>
              <IconV2 name='info-static' color={publicaColors.gray} />
            </div>
          </Tooltip>
        </Col>
      )}
      {logicKey && (
        <Col>
          <LogicToggle
            operator={operator}
            handleToggleOperator={handleToggleOperator}
            key={logicKey}
          />
        </Col>
      )}
      {allowKey && (
        <Col>
          <IncludeExcludeToggle
            status={status}
            handleToggleStatus={handleToggleStatus}
            key={allowKey}
          />
        </Col>
      )}
    </Cols>
  );
};
